import React, { useState, useEffect } from 'react';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import EvalOverview from './EvalOverview';
import EvalQuestionnaire from './EvalQuestionnaire';
import EvalChallenge from './EvalChallenge';
import EvalSettings from './EvalSettings';
import CreateEvaluationWizard from '../CreateEvaluationComponents/CreateEvaluationWizard';
import EvalStatus from './Utils/EvalStatus';

/**
 * EvalDetails Component
 * Reusable component for displaying evaluation details for both talents and candidates
 */
const EvalDetails = ({ 
  evalId,
  contextType = 'talent' // 'talent' or 'candidate'
}) => {
  // State for active tab
  const [activeTab, setActiveTab] = useState('overview');
  
  // State for eval data
  const [evalData, setEvalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Extracted data objects for questionnaire and challenge
  const [challengeData, setChallengeData] = useState(null);
  const [questionnaireData, setQuestionnaireData] = useState(null);
  
  // State for CreateEvaluationWizard
  const [showCreateEvaluationWizard, setShowCreateEvaluationWizard] = useState(false);
  
  // Session context for authentication
  const { user } = useSession();

  // Function to fetch eval details from the API
  const fetchEvalDetails = async () => {
    setLoading(true);
    
    try {
      // Check if user exists
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }
      
      // Make GET request to the eval details endpoint with eval ID as a parameter
      const url = new URL(API_ENDPOINTS.GET_EVAL_DETAILS);
      url.searchParams.append('eid', evalId);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.access_token}`
        }
      });
      
      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }
      
      // Parse the response data
      const data = await response.json();
      
      // Log the data for debugging
      console.log(`${contextType} eval details data:`, data);
      
      // Transform the data if needed
      const transformedData = Array.isArray(data) && data.length > 0 ? data[0] : data;

      // Extract challenge and questionnaire data from the new structure
      if (transformedData) {
        // Extract challenge data directly from challenge_instance array
        if (transformedData.challenge_instance && transformedData.challenge_instance.length > 0) {
          setChallengeData(transformedData.challenge_instance[0]);
        }
        
        // Extract questionnaire data directly from questionnaire_instance array
        if (transformedData.questionnaire_instance && transformedData.questionnaire_instance.length > 0) {
          setQuestionnaireData(transformedData.questionnaire_instance[0]);
        }
      }

      setEvalData(transformedData);
      setError(null);
    } catch (err) {
      console.error(`Error fetching ${contextType} eval details:`, err);
      
      // If it's a 401 error, it might be an expired session
      if (err.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError('Failed to load evaluation details. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch eval details when evalId changes
  useEffect(() => {
    if (evalId) {
      fetchEvalDetails();
    }
  }, [evalId]);

  // If no eval is selected, show a message
  if (!evalId) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16 bg-gray-50 rounded-lg border border-gray-200 shadow-inner">
        <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-gray-500 font-medium mb-2">No Evaluation Selected</p>
        <p className="text-gray-400 text-sm max-w-md text-center">
          Select an evaluation from the list on the left to view its details
        </p>
      </div>
    );
  }

  return (
    <div className="h-full">
      {/* Eval header */}
      <div className="mb-6 pb-4 border-b border-gray-100">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center">
              {/* Icon based on candidate_id presence */}
              <div className="relative mr-3 flex-shrink-0">
                {evalData?.candidate_id ? (
                  // Company/Job icon when candidate_id is present
                  <div 
                    className="cursor-help" 
                    title="Job Evaluation - This evaluation is associated with a job position"
                  >
                    <svg 
                      className="w-6 h-6 text-gray-600" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                ) : (
                  // User/Person icon when candidate_id is not present
                  <div 
                    className="cursor-help" 
                    title="Talent Evaluation - This evaluation is for an individual talent"
                  >
                    <svg 
                      className="w-6 h-6 text-gray-600" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                {evalData?.evaluations?.name }
              </h2>
            </div>
          </div>
          
          {/* Right side with dropdown and main button */}
          <div className="flex items-center space-x-3">
            {/* Quick Links Dropdown */}
            <div className="relative group">
              <button className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:border-gray-400 transition-colors flex items-center">
                <span>Quick Links</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Dropdown Menu */}
              <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                <div className="py-1">
                  {evalData && evalData.candidates && evalData.candidates.job_id && (
                    <a 
                      href={`/job-details?jid=${evalData.candidates.job_id}`}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    >
                      Job Details
                    </a>
                  )}
                  
                  {evalData && evalData.candidates && evalData.candidates.job_id && (
                    <a 
                      href={`/candidate-details?cid=${evalData.candidate_id}`}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    >
                      Candidate Profile
                    </a>
                  )}
                  
                  {evalData && (
                    <a 
                      href={`/talentdetails?tid=${evalData.talent_id}`}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    >
                      Talent Profile
                    </a>
                  )}
                  
                  {evalData && evalData.evaluations && (
                    <a 
                      href={`/evaluation-details?eid=${evalData.evaluation_id}`}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                    >
                      Evaluation Template
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Main Action Button */}
            {evalData && (
              evalData.evaluations?.status === 'evs_available' ? (
                <a
                  href={`https://eval.tendent.ai/${evalData.eval_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-black text-white font-semibold rounded hover:bg-gray-800 transition-colors text-sm flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Unique Evaluation Link
                </a>
              ) : (
                <button
                  onClick={() => setShowCreateEvaluationWizard(true)}
                  className="px-4 py-2 bg-yellow-600 text-white font-semibold rounded hover:bg-yellow-700 transition-colors text-sm flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Finalize Evaluation
                </button>
              )
            )}
          </div>
        </div>
        
        {/* Status Badge and Info */}
        <div className="flex items-center mt-3 space-x-4">
          {evalData && evalData.status && (
            <EvalStatus status={evalData.status} showDot={true} />
          )}
          <div className="text-sm text-gray-500">
            <span className="font-bold">Eval ID:</span> {evalId}
          </div>
          {evalData && (
            <div className="text-sm text-gray-500">
              <span className="font-bold">Assigned on:</span> {new Date(evalData.created_at).toLocaleString()}
            </div>
          )}
        </div>
      </div>
      
      {/* Tab navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-6">
          <button
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('overview')}
          >
            Evaluation Overview
          </button>
          <button
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'questionnaire'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('questionnaire')}
          >
            Questionnaire Response
          </button>
          <button
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'challenge'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('challenge')}
          >
            Challenge Response
          </button>
          <button
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'settings'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
        </nav>
      </div>
      
      {/* Tab content */}
      <div className="min-h-[500px] bg-white">
        {loading ? (
          <div className="flex flex-col justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-500">Loading evaluation details...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col justify-center items-center h-64">
            <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-4 max-w-md">
              <p className="font-medium">Error loading details</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
            <button 
              onClick={() => fetchEvalDetails()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div>
            {activeTab === 'overview' && (
              <EvalOverview 
                questionnaireData={questionnaireData}
                challengeData={challengeData}
                evalData={evalData}
                contextType={contextType}
              />
            )}
            
            {activeTab === 'questionnaire' && (
              <EvalQuestionnaire 
                questionnaireData={questionnaireData}
                contextType={contextType}
              />
            )}
            
            {activeTab === 'challenge' && (
              <EvalChallenge 
                challengeData={challengeData}
                contextType={contextType}
              />
            )}
            
            {activeTab === 'settings' && (
              <EvalSettings 
                evalId={evalId}
                evalData={evalData}
                onRefresh={fetchEvalDetails}
              />
            )}
          </div>
        )}
      </div>

      {/* Create Evaluation Wizard */}
      {showCreateEvaluationWizard && (
        <CreateEvaluationWizard
          isOpen={showCreateEvaluationWizard}
          onClose={() => setShowCreateEvaluationWizard(false)}
          evaluation_id={evalData?.evaluations?.evaluation_id}
        />
      )}
    </div>
  );
};

export default EvalDetails;
